import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CoreButton from '@components/Buttons/CoreButton';
import TextInput from '@components/Fields/TextInput';
import ModalContent from '@components/Modals/components/ModalContent';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAcceptJs } from 'react-acceptjs';
import { makeStyles } from 'tss-react/mui';
import { Grid } from '@mui/material';
import useAppState from '@hooks/API/useAppState';
import LoadingLogo from '@components/Modals/LoadingModal/Components/LoadingLogo';
import toRealMoney from '@helpers/toRealMoney';
import beginAuthorizeNetDeposit from '@api/payment/beginAuthorizeNetDeposit';
import { useUserState } from '@context/UserContext';
import getPaymentStatusFromOrderState from '@components/Modals/helpers/getPaymentStatusFromOrderState';
import useGetBalance from '@components/Hooks/useGetBalance';
import { captureMessage } from '@sentry/react';
import completeAuthorizeNetDeposit from '@api/payment/completeAuthorizeNetDeposit';
const useStyles = makeStyles()((theme) => ({
    finePrint: {
        textAlign: 'center',
        fontSize: '12px',
        color: theme.palette.grey[500],
    },
    poweredBy: {
        textAlign: 'center',
        color: theme.palette.grey[500],
        marginTop: '20px',
    },
    logo: {
        height: '16px',
        marginLeft: '3px',
        verticalAlign: 'middle',
        paddingBottom: '2px',
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '200px',
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '16px',
    },
    hidden: {
        display: 'none',
    },
}));
const authData = {
    apiLoginID: process.env.AUTHORIZENET_API_LOGIN_ID || '',
    clientKey: process.env.AUTHORIZEDNET_CLIENT_KEY || '',
};
const AuthorizeNetPayment = ({ onFinishProcess, setError, setPaymentStatus, setTransactionWarningModalData, setPaypalOrderId, paypalOrderId, currencyAmount, currency, transactionAmount, paymentStatus, }) => {
    const { classes } = useStyles();
    const { dispatchData, loading, error } = useAcceptJs({
        authData,
        environment: process.env.AUTHORIZENET_ENV,
    });
    const [cardData, setCardData] = useState({
        cardNumber: '',
        month: '',
        year: '',
        cardCode: '',
        zip: '',
        fullName: '',
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { paymentMethods, isLoading, error: paymentMethodsError } = useAppState();
    const { userDetails } = useUserState();
    const { refetch: refetchBalance } = useGetBalance();
    const { fixedFee, percentFee } = useMemo(() => { var _a; return (_a = paymentMethods === null || paymentMethods === void 0 ? void 0 : paymentMethods.paypal) !== null && _a !== void 0 ? _a : { fixedFee: 0, percentFee: 0 }; }, [paymentMethods]);
    const percentageFee = useMemo(() => percentFee * 0.01, [percentFee]);
    const minAmount = useMemo(() => (currency === 'PM' ? 5 : 10), [currency]);
    const calculateTransactionUSDAmountWithFees = useCallback((transactionAmountInput) => {
        const transactionUSDAmountWithFees = (transactionAmountInput + fixedFee) / (1.0 - percentageFee);
        return Math.round(transactionUSDAmountWithFees * 100) / 100;
    }, [fixedFee, percentageFee]);
    const transactionAmountWithFee = useMemo(() => calculateTransactionUSDAmountWithFees(transactionAmount), [calculateTransactionUSDAmountWithFees, transactionAmount]);
    useEffect(() => {
        if (paymentStatus === 'ACCEPTED' && paypalOrderId) {
            void completeTransaction({ id: paypalOrderId });
        }
    }, [paymentStatus, paypalOrderId]);
    useEffect(() => {
        if (error) {
            setError({ errorMessage: 'Error initializing Authorize.net', allowRetry: false });
        }
    }, [error]);
    if (paymentMethodsError || (!isLoading && !paymentMethods)) {
        return (_jsx(ModalContent, { align: "center", children: _jsxs("div", { className: classes.loading, children: [_jsx(LoadingLogo, {}), _jsx("p", { children: "An error has occurred, please refresh the page and try again" })] }) }));
    }
    if (isLoading || !paymentMethods) {
        return (_jsx(ModalContent, { align: "center", children: _jsxs("div", { className: classes.loading, children: [_jsx(LoadingLogo, {}), _jsx("p", { children: "Processing" })] }) }));
    }
    // Validation functions
    const validateCardNumber = (cardNumber) => {
        if (!cardNumber.trim())
            return 'Card number is required';
        const cleanedNumber = cardNumber.replace(/\s/g, '');
        if (!/^\d{13,16}$/.test(cleanedNumber))
            return 'Card number must be 13-16 digits';
        return undefined;
    };
    const validateExpirationMonth = (month) => {
        if (!month.trim())
            return 'Exp month is required';
        const monthNum = parseInt(month, 10);
        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12)
            return 'Month must be 1-12';
        return undefined;
    };
    const validateExpirationYear = (year) => {
        if (!year.trim())
            return 'Exp year is required';
        // Must be exactly 2 digits
        if (!/^\d{2}$/.test(year.trim()))
            return 'Year must be 2 digits';
        const currentYear = new Date().getFullYear();
        const yearNum = parseInt(year, 10);
        if (isNaN(yearNum))
            return 'Invalid year format';
        // Convert 2-digit year to 4-digit year
        const fullYear = 2000 + yearNum;
        if (fullYear < currentYear)
            return 'Card has expired';
        if (fullYear > currentYear + 20)
            return 'Year too far in future';
        return undefined;
    };
    const validateCardCode = (cardCode) => {
        if (!cardCode.trim())
            return undefined;
        if (!/^\d{3,4}$/.test(cardCode))
            return 'Security code must be 3-4 digits';
        return undefined;
    };
    const validateZipCode = (zip) => {
        if (!zip.trim())
            return undefined;
        if (zip.length > 20)
            return 'Zip code must be 20 characters or less';
        if (!/^[a-zA-Z0-9\s\-']+$/.test(zip))
            return 'Zip code contains invalid characters';
        return undefined;
    };
    const validateFullName = (fullName) => {
        if (!fullName.trim())
            return undefined;
        if (fullName.trim().length > 64)
            return 'Name must be 64 characters or less';
        if (!/^[a-zA-Z0-9\s\-']+$/.test(fullName))
            return 'Name contains invalid characters';
        return undefined;
    };
    const validateExpirationDate = (month, year) => {
        // Only validate if both month and year are provided and valid format
        if (!month.trim() || !year.trim())
            return undefined;
        if (!/^\d{1,2}$/.test(month.trim()) || !/^\d{2}$/.test(year.trim()))
            return undefined;
        const monthNum = parseInt(month, 10);
        const yearNum = parseInt(year, 10);
        // Check if month and year are in valid ranges
        if (monthNum < 1 || monthNum > 12)
            return undefined;
        if (yearNum < 0 || yearNum > 99)
            return undefined;
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11
        const fullYear = 2000 + yearNum;
        // Check if the expiration date is in the past
        if (fullYear < currentYear || (fullYear === currentYear && monthNum < currentMonth)) {
            return 'Card has expired';
        }
        return undefined;
    };
    // Helper function to get basic field validation error
    const getBasicFieldError = (fieldName, value) => {
        switch (fieldName) {
            case 'cardNumber':
                return validateCardNumber(value);
            case 'month':
                return validateExpirationMonth(value);
            case 'year':
                return validateExpirationYear(value);
            case 'cardCode':
                return validateCardCode(value);
            case 'zip':
                return validateZipCode(value);
            case 'fullName':
                return validateFullName(value);
        }
    };
    // Helper function to validate expiration fields with combined date check
    const validateExpirationField = (fieldName, value) => {
        const basicError = getBasicFieldError(fieldName, value);
        if (basicError)
            return basicError;
        const otherValue = fieldName === 'month' ? cardData.year : cardData.month;
        const monthValue = fieldName === 'month' ? value : otherValue;
        const yearValue = fieldName === 'year' ? value : otherValue;
        return validateExpirationDate(monthValue, yearValue);
    };
    // Helper function to re-validate other expiration field
    const revalidateOtherExpirationField = (fieldName, value, otherField, otherValue) => {
        const otherBasicError = getBasicFieldError(otherField, otherValue);
        if (otherBasicError)
            return otherBasicError;
        const monthValue = fieldName === 'month' ? value : otherValue;
        const yearValue = fieldName === 'year' ? value : otherValue;
        return validateExpirationDate(monthValue, yearValue);
    };
    const validateField = (fieldName, value) => {
        const isExpirationField = fieldName === 'month' || fieldName === 'year';
        const fieldError = isExpirationField
            ? validateExpirationField(fieldName, value)
            : getBasicFieldError(fieldName, value);
        // Handle cross-field validation for expiration fields
        if (isExpirationField && !fieldError) {
            const otherField = fieldName === 'month' ? 'year' : 'month';
            const otherValue = fieldName === 'month' ? cardData.year : cardData.month;
            setValidationErrors((prev) => {
                const newErrors = { ...prev, [fieldName]: fieldError };
                if (prev[otherField] === 'Card has expired') {
                    const otherError = revalidateOtherExpirationField(fieldName, value, otherField, otherValue);
                    newErrors[otherField] = otherError;
                }
                return newErrors;
            });
        }
        else {
            setValidationErrors((prev) => ({ ...prev, [fieldName]: fieldError }));
        }
        return !fieldError;
    };
    const validateAllFields = () => {
        const errors = {};
        errors.cardNumber = validateCardNumber(cardData.cardNumber);
        errors.month = validateExpirationMonth(cardData.month);
        errors.year = validateExpirationYear(cardData.year);
        errors.cardCode = validateCardCode(cardData.cardCode);
        errors.zip = validateZipCode(cardData.zip);
        errors.fullName = validateFullName(cardData.fullName);
        // Check combined expiration date validation
        if (!errors.month && !errors.year) {
            const expirationError = validateExpirationDate(cardData.month, cardData.year);
            if (expirationError) {
                // Show the error on both month and year fields
                errors.month = expirationError;
                errors.year = expirationError;
            }
        }
        setValidationErrors(errors);
        return !Object.values(errors).some((value) => value !== undefined);
    };
    const handleError = async () => {
        if (paypalOrderId && paymentStatus !== 'WAITING') {
            try {
                await completeAuthorizeNetDeposit({
                    transactionId: paypalOrderId,
                    newOrderState: 'error',
                });
            }
            catch {
                // This is a best effort attempt to update the Order with the error state
            }
        }
        setPaymentStatus('ERROR');
        setPaypalOrderId(undefined);
        onFinishProcess();
    };
    const handleMinAmountNotMet = () => {
        setError({
            errorMessage: `The minimum amount for this transaction is $${toRealMoney(minAmount)} USD`,
            allowRetry: true,
        });
        return false;
    };
    const resetCardData = () => {
        setCardData({
            cardNumber: '',
            month: '',
            year: '',
            cardCode: '',
            zip: '',
            fullName: '',
        });
        setValidationErrors({});
        setIsSubmitting(false);
    };
    const createTransaction = async ({ dataDescriptor, dataValue }) => {
        var _a, _b, _c;
        if (!transactionAmount) {
            setPaymentStatus('ERROR');
            setError({ errorMessage: 'Please provide an amount for this transaction', allowRetry: true });
            return false;
        }
        const value = (transactionAmount + fixedFee) / (1.0 - percentageFee);
        if (value < minAmount) {
            return handleMinAmountNotMet();
        }
        try {
            const { orderState, paypalOrderId: newPaypalOrderId } = await beginAuthorizeNetDeposit({
                transactionType: currency === 'USD' ? 'deposit' : 'purchase',
                amount: transactionAmount,
                coins: currency === 'PM' ? currencyAmount : null,
                dataDescriptor,
                dataValue,
                state: userDetails === null || userDetails === void 0 ? void 0 : userDetails.state,
                country: userDetails === null || userDetails === void 0 ? void 0 : userDetails.country,
            });
            const status = getPaymentStatusFromOrderState(orderState);
            setPaymentStatus(status);
            setPaypalOrderId(newPaypalOrderId);
            return true;
        }
        catch (err) {
            const axiosErr = err;
            const dataResponse = (_a = axiosErr.response) === null || _a === void 0 ? void 0 : _a.data;
            // set state to show deposit warning modal when user violate deposit rule
            if (dataResponse && dataResponse.name === 'DEPOSIT_LIMIT_REACHED') {
                setTransactionWarningModalData({
                    isShow: true,
                    nextLifted: (_b = dataResponse.nextLifted) !== null && _b !== void 0 ? _b : null,
                    errorCode: (_c = dataResponse.name) !== null && _c !== void 0 ? _c : null,
                });
            }
            throw err;
        }
    };
    const completeTransaction = async (data) => {
        setPaymentStatus('VALIDATING');
        if (!(data === null || data === void 0 ? void 0 : data.id)) {
            captureMessage('Successful authorize.net transaction but no transaction ID', 'fatal');
            setError({
                errorMessage: 'Something went wrong after your payment was taken. Please contact support immediately.',
                allowRetry: false,
            });
            void handleError();
            return;
        }
        let completeAuthorizeNetDepositResponse;
        try {
            completeAuthorizeNetDepositResponse = await completeAuthorizeNetDeposit({
                transactionId: data.id,
                newOrderState: 'completed',
            });
        }
        catch {
            setError({
                errorMessage: 'Something went wrong after your payment was taken. Please contact support immediately.',
                allowRetry: false,
            });
            void handleError();
            return;
        }
        setPaymentStatus(getPaymentStatusFromOrderState(completeAuthorizeNetDepositResponse.orderState));
        void refetchBalance();
        onFinishProcess();
    };
    const handleSubmit = async (event) => {
        event.preventDefault();
        // Validate all fields before submitting
        if (!validateAllFields()) {
            return;
        }
        setIsSubmitting(true);
        try {
            // Dispatch CC data to Authorize.net and receive payment nonce for use on your server
            const response = await dispatchData({ cardData });
            if (response.messages.resultCode === 'Ok') {
                await createTransaction(response.opaqueData);
            }
            else {
                setError({ errorMessage: response.messages.message[0].text, allowRetry: false });
                void handleError();
            }
        }
        catch {
            void handleError();
        }
        finally {
            resetCardData();
        }
    };
    return (_jsxs(ModalContent, { children: [paymentStatus === 'VALIDATING' && (_jsxs("div", { className: classes.loading, children: [_jsx(LoadingLogo, {}), _jsx("p", { children: "Processing Payment" })] })), _jsxs("form", { "data-testid": "authorize-net-form", className: paymentStatus === 'VALIDATING' ? classes.hidden : '', onSubmit: handleSubmit, children: [_jsxs(Grid, { container: true, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(TextInput, { id: "authorize-net-card-number", "data-ignore": true, placeholder: "Card Number", value: cardData.cardNumber, onChange: (e) => {
                                        const value = e.target.value;
                                        setCardData({ ...cardData, cardNumber: value });
                                        validateField('cardNumber', value);
                                    }, onBlur: (e) => validateField('cardNumber', e.target.value), "aria-label": `Authorize.net`, type: "text", error: !!validationErrors.cardNumber, errorMessage: validationErrors.cardNumber, errorMessageAlign: "left", disabled: isSubmitting || paymentStatus === 'VALIDATING' }) }), _jsx(Grid, { item: true, xs: 12, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 6, children: _jsx(TextInput, { id: "authorize-net-expiration-month", "data-ignore": true, placeholder: "Exp Month", value: cardData.month, onChange: (e) => {
                                                    let value = e.target.value;
                                                    // Remove any non-digit characters
                                                    value = value.replace(/\D/g, '');
                                                    // Limit to 2 characters maximum for month (01-12)
                                                    if (value.length > 2) {
                                                        value = value.slice(0, 2);
                                                    }
                                                    setCardData({ ...cardData, month: value });
                                                    validateField('month', value);
                                                }, onBlur: (e) => validateField('month', e.target.value), onPaste: (e) => {
                                                    e.preventDefault();
                                                    const paste = e.clipboardData.getData('text');
                                                    let value = paste.replace(/\D/g, '');
                                                    if (value.length > 2) {
                                                        value = value.slice(0, 2);
                                                    }
                                                    setCardData({ ...cardData, month: value });
                                                    validateField('month', value);
                                                }, "aria-label": `Authorize.net`, type: "text", error: !!validationErrors.month, errorMessage: validationErrors.month, errorMessageAlign: "left", disabled: isSubmitting || paymentStatus === 'VALIDATING' }) }), _jsx(Grid, { item: true, xs: 6, children: _jsx(TextInput, { id: "authorize-net-expiration-year", "data-ignore": true, placeholder: "Exp Year", value: cardData.year, onChange: (e) => {
                                                    let value = e.target.value;
                                                    // Remove any non-digit characters
                                                    value = value.replace(/\D/g, '');
                                                    // Limit to 2 characters maximum
                                                    if (value.length > 2) {
                                                        value = value.slice(0, 2);
                                                    }
                                                    setCardData({ ...cardData, year: value });
                                                    validateField('year', value);
                                                }, onBlur: (e) => validateField('year', e.target.value), onPaste: (e) => {
                                                    e.preventDefault();
                                                    const paste = e.clipboardData.getData('text');
                                                    let value = paste.replace(/\D/g, '');
                                                    if (value.length > 2) {
                                                        value = value.slice(0, 2);
                                                    }
                                                    setCardData({ ...cardData, year: value });
                                                    validateField('year', value);
                                                }, "aria-label": `Authorize.net`, type: "text", error: !!validationErrors.year, errorMessage: validationErrors.year, errorMessageAlign: "left", disabled: isSubmitting || paymentStatus === 'VALIDATING' }) })] }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextInput, { id: "authorize-net-card-code", "data-ignore": true, placeholder: "CSC", value: cardData.cardCode, onChange: (e) => {
                                        const value = e.target.value;
                                        setCardData({ ...cardData, cardCode: value });
                                        validateField('cardCode', value);
                                    }, onBlur: (e) => validateField('cardCode', e.target.value), "aria-label": `Authorize.net`, type: "text", error: !!validationErrors.cardCode, errorMessage: validationErrors.cardCode, errorMessageAlign: "left", disabled: isSubmitting || paymentStatus === 'VALIDATING' }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextInput, { id: "authorize-net-full-name", "data-ignore": true, placeholder: "Full Name", value: cardData.fullName, onChange: (e) => {
                                        const value = e.target.value;
                                        setCardData({ ...cardData, fullName: value });
                                        validateField('fullName', value);
                                    }, onBlur: (e) => validateField('fullName', e.target.value), "aria-label": `Authorize.net`, type: "text", error: !!validationErrors.fullName, errorMessage: validationErrors.fullName, errorMessageAlign: "left", disabled: isSubmitting || paymentStatus === 'VALIDATING' }) }), _jsx(Grid, { item: true, xs: 12, children: _jsx(TextInput, { id: "authorize-net-zip", "data-ignore": true, placeholder: "Zip Code", value: cardData.zip, onChange: (e) => {
                                        const value = e.target.value;
                                        setCardData({ ...cardData, zip: value });
                                        validateField('zip', value);
                                    }, onBlur: (e) => validateField('zip', e.target.value), "aria-label": `Authorize.net`, type: "text", error: !!validationErrors.zip, errorMessage: validationErrors.zip, errorMessageAlign: "left", disabled: isSubmitting || paymentStatus === 'VALIDATING' }) })] }), _jsx("div", { className: classes.buttonContainer, children: _jsx(CoreButton, { variant: "primary", type: "submit", loading: loading || isSubmitting, disabled: isSubmitting || paymentStatus === 'VALIDATING', children: `Pay $${transactionAmountWithFee}` }) }), _jsxs("p", { className: classes.poweredBy, children: ["Powered by ", _jsx("img", { className: classes.logo, src: '/img/authorize-net.svg', alt: "Authorize.net" })] }), _jsxs("p", { className: classes.finePrint, children: ["Deposit Fee: ", fixedFee.toLocaleString(undefined, { style: 'currency', currency: 'USD' }), " +", ' ', percentageFee.toLocaleString(undefined, { style: 'percent', minimumFractionDigits: 1 }), " of the transaction amount"] })] })] }));
};
export default AuthorizeNetPayment;
